import React from "react";
import SeoHead from "../components/SeoHead";
import { SUPPORTED_COUNTRIES } from "../seo/siteData";

const TermsOfUsePage = () => {
    return (
        <>
        <SeoHead
            title="Tiply Terms of Use"
            description="Terms governing Tiply's digital tipping platform, fees, supported euro-area countries, payouts, verification and user responsibilities."
            canonicalPath="/terms/"
            localizedAlternates={false}
            schemaType="WebPage"
        />
        <div className="max-w-4xl mx-auto px-4 py-10 text-gray-800">
            <h1 className="text-3xl font-bold mb-4">Tiply Terms of Use</h1>
            <p className="text-sm text-gray-600 mb-8">Effective Date: 11.08.2026</p>

            <p className="mb-4">
                Welcome to Tiply, a digital tipping platform operated by Tiply OÜ ("we", "us", or "our"), a private limited company registered in Estonia under company number 16652928, with its registered office at Juhkentali 8, Tallinn 10132, Estonia. These platform terms of use ("Terms of Use") apply to your access to and use of Tiply’s platform, available via our website at tiply.ee and any other web applications or materials provided by us (collectively, the "Platform").
            </p>
            <p className="mb-4">
                By using the Platform, you agree to be bound by these Terms of Use and our Privacy Policy (together, the "Agreement"). If you do not accept these terms, you should not use the Platform. These English-language Terms are the authoritative version.
            </p>

            <Section title="1. Overview">
                <p className="mb-2">Tiply enables organizations (such as restaurants, bars, cafes, or similar establishments) to facilitate the collection and distribution of voluntary gratuities ("Tips") from their customers to individuals working in their organization.</p>
                <BulletList items={[
                    "Unique QR codes for tip collection",
                    "A web-based tipping interface",
                    "A management dashboard for organizational users",
                    "A personal interface for individuals to view tip data and update information",
                    "All payments are processed by Stripe or Montonio UAB, our licensed payment service provider.",
                ]} />
            </Section>

            <Section title="2. User Types">
                <p className="mb-2">These Terms apply to all users of the Platform, including:</p>
                <BulletList items={[
                    "Organizations who use Tiply to manage tip collection",
                    "Individuals who are added to the Platform by an Organization and authorized to receive Tips via the Platform",
                    "Payers (customers) who voluntarily submit Tips via the Platform",
                ]} />
            </Section>

            <Section title="3. Account Registration">
                <Subsection title="For Organizations:">
                    <p>To access the Platform, you must complete our onboarding form or register online, providing accurate details such as:</p>
                    <BulletList items={["Legal entity name and display name", "Address", "Representative contact details"]} />
                    <p className="mt-2">We reserve the right to verify any submitted information and to suspend or restrict access where necessary.</p>
                </Subsection>
                <Subsection title="For Individuals:">
                    <p>Individuals are added to the Platform by an Organization. Each Individual receives access to a personalized interface to:</p>
                    <BulletList items={["View tip activity", "Edit personal details (such as payout IBAN)", "Access history and statistics"]} />
                    <p className="mt-2">Tiply is not the employer of any Individual on the Platform. All employment or contractual matters are strictly between the Individual and the Organization.</p>
                </Subsection>
            </Section>

            <Section title="4. Geographic Availability and Eligibility">
                <p className="mb-3">
                    Tiply is currently offered to eligible Organizations and Individuals in the following EU euro-area countries:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mb-4">
                    {SUPPORTED_COUNTRIES.map((country) => <div key={country}>• {country}</div>)}
                </div>
                <p>
                    Availability remains subject to successful onboarding and identity verification, compatible EUR payout details, payment-provider and banking availability, and applicable law. Tiply may request additional information or refuse, restrict, suspend, or discontinue availability where operationally or legally required. Listing a country does not mean Tiply is endorsed, licensed, or approved by that country's regulator.
                </p>
            </Section>

            <Section title="5. Tip Collection, Fees and Payments">
                <BulletList items={[
                    "Tips are voluntary and must not be linked to the provision of any goods or services.",
                    "Payments are processed via Stripe or Montonio and temporarily routed via Tiply’s account for onward transfer to the Individual.",
                    "Tips are distributed to the payout details (IBAN) provided by the Individual.",
                    "Tiply deducts a 7% platform fee plus a fixed €0.25 processing fee from each Tip before distribution.",
                    "Payouts typically occur at the end of each business day, subject to payment service provider and banking partner processing times.",
                ]} />

                <p className="mt-2 font-semibold">Payout Timing:</p>
                <ul className="list-disc ml-6 mt-1 space-y-1">
                    <li>The first payout to a new Organization or Individual may be delayed by 7–14 days due to mandatory verification checks by our payment service provider or banking partner.</li>
                    <li>After verification, payouts are initiated daily. However, each payout covers Tips that were processed approximately three (3) business days earlier (rolling settlement delay).</li>
                    <li>Example: a Tip received on 1 July will typically be included in the payout on 4 July; a Tip received on 2 July will typically be included in the payout on 5 July.</li>
                </ul>

                <p className="mt-2 font-semibold">Important:</p>
                <p className="mt-1">
                    Both Organizations and Individuals are responsible for ensuring the accuracy of their submitted information, especially IBAN details. Tiply is not liable for delays or failed payouts resulting from incorrect or outdated data, or from delays imposed by third-party payment service providers or banks.
                </p>
            </Section>

            <Section title="6. Use of QR Codes">
                <p>Each Individual is assigned a unique QR code for tip collection. QR cards can be printed and distributed by the Organization or requested via Tiply. QR codes must not be modified or used for any unauthorized purpose.</p>
            </Section>

            <Section title="7. Prohibited Uses">
                <p>You must not:</p>
                <BulletList items={[
                    "Use the Platform to collect payments for goods or services",
                    "Provide false, misleading, or fraudulent information",
                    "Impersonate others or create duplicate accounts",
                    "Disrupt the Platform through technical or social engineering means",
                    "Violate any applicable laws or regulations",
                ]} />
                <p className="mt-2">We reserve the right to suspend or terminate access for violations of these Terms.</p>
            </Section>

            <Section title="8. Data Protection">
                <p>We process personal data in accordance with our <a href="/privacy" className="text-blue-600 underline">Privacy Policy</a>. Tiply complies with Regulation (EU) 2016/679 (GDPR). By using the Platform, you consent to Tiply’s handling of your data as described therein.</p>
            </Section>

            <Section title="9. Intellectual Property">
                <p>All materials and content on the Platform, including text, images, graphics, logos, code, and trademarks, are owned or licensed by Tiply and protected by intellectual property laws. Users may not reproduce, copy, distribute, or exploit any content without express written consent.</p>
            </Section>

            <Section title="10. Liability and Disclaimer">
                <p>Tiply provides the Platform on an "as-is" and "as-available" basis. While we strive to provide a secure and stable service, we make no guarantees regarding uptime, error-free performance, or security.</p>
                <p className="mt-2">To the maximum extent permitted by law, Tiply disclaims all warranties and is not liable for:</p>
                <BulletList items={[
                    "Any indirect, incidental, or consequential loss",
                    "Loss of earnings, data, or reputation",
                    "Issues arising from third-party payment services (e.g., Stripe, Montonio)",
                    "Incorrect data submitted by Users",
                ]} />
                <p className="mt-2">Our maximum liability shall not exceed €500.</p>
            </Section>

            <Section title="11. Tax, Payroll and Legal Responsibility">
                <p>
                    Tiply does not provide tax, payroll, accounting, or legal advice. Organizations and Individuals are responsible for determining and meeting all reporting, employment, social security, and tax obligations that apply to Tips in their country. Any calculator or comparison shown on the Platform is an illustrative Estonia-specific example and must not be relied on for another jurisdiction.
                </p>
            </Section>

            <Section title="12. Modification and Termination">
                <p>Tiply may update these Terms at any time. Material changes will be posted on the Platform. Continued use constitutes acceptance. If you disagree with updated terms, you must discontinue use.</p>
                <p className="mt-2">We may suspend or terminate any account at our discretion, especially in cases of suspected fraud, abuse, or breach of these Terms.</p>
            </Section>

            <Section title="13. Governing Law">
                <p>These Terms are governed by the laws of the Republic of Estonia. Any disputes shall be resolved in Harju County Court.</p>
            </Section>
            <Section title="14. Pricing">
                <p className="mb-2">
                    Tiply does not charge Organizations a setup fee or monthly subscription fee for the standard Platform.
                </p>
                <BulletList items={[
                    "A 7% platform fee plus a fixed €0.25 processing fee is deducted from each Tip transaction.",
                    "The fee covers payment processing and use of the Tiply service.",
                    "The amount distributed to the recipient is therefore the Tip amount less the applicable transaction fee."
                ]} />
            </Section>
            <Section title="15. Contact">
                <p>For questions about these Terms, contact us at <a href="mailto:info@tiply.ee" className="text-blue-600 underline">info@tiply.ee</a>.</p>
            </Section>

        </div>
        </>
    );
};

const Section = ({ title, children }) => (
    <div className="mb-8">
        <h2 className="text-xl font-semibold mb-2">{title}</h2>
        {children}
    </div>
);

const Subsection = ({ title, children }) => (
    <div className="mb-4">
        <h3 className="font-medium">{title}</h3>
        {children}
    </div>
);

const BulletList = ({ items }) => (
    <ul className="list-disc ml-6 mt-2 space-y-1">
        {items.map((item, i) => <li key={i}>{item}</li>)}
    </ul>
);

export default TermsOfUsePage;
